import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import {
  BankAccountInfo,
  BankBooking,
  BankStatementImportRecord,
  BankSuggestion,
  BankTransactionFilters,
  BankTransactionRecord,
  BankTransactionsResponse
} from 'shared';

/**
 * Admin bank-statement import + reconciliation.
 *
 * Scoped server-side to the request's branding/config domain, so every call
 * operates on the current site's own bank account (Api::AdminBankController).
 * "Match" only links a transaction to an order; "book" is the separate step that
 * confirms the payment and moves money.
 */
@Injectable({ providedIn: 'root' })
export class BankReconciliationService {
  private http = inject(HttpClient);

  getAccount(): Observable<{ account: BankAccountInfo | null }> {
    return this.http.get<{ account: BankAccountInfo | null }>('/admin/bank/account');
  }

  getImports(): Observable<{ imports: BankStatementImportRecord[] }> {
    return this.http.get<{ imports: BankStatementImportRecord[] }>('/admin/bank/imports');
  }

  /** Upload a statement CSV. Angular sets the multipart content-type for FormData. */
  uploadCsv(file: File): Observable<{ import: BankStatementImportRecord }> {
    const form = new FormData();
    form.append('file', file, file.name);
    return this.http.post<{ import: BankStatementImportRecord }>('/admin/bank/imports', form);
  }

  getTransactions(filters: BankTransactionFilters = {}): Observable<BankTransactionsResponse> {
    let params = new HttpParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params = params.set(key, String(value));
      }
    });
    return this.http.get<BankTransactionsResponse>('/admin/bank/transactions', { params });
  }

  getOpenBookings(): Observable<{ open_bookings: BankBooking[] }> {
    return this.http.get<{ open_bookings: BankBooking[] }>('/admin/bank/open_bookings');
  }

  getSuggestions(transactionId: number): Observable<{ suggestions: BankSuggestion[] }> {
    return this.http.get<{ suggestions: BankSuggestion[] }>(
      `/admin/bank/transactions/${transactionId}/suggestions`
    );
  }

  match(transactionId: number, orderId: number): Observable<{ transaction: BankTransactionRecord }> {
    return this.http.post<{ transaction: BankTransactionRecord }>(
      `/admin/bank/transactions/${transactionId}/match`,
      { order_id: orderId }
    );
  }

  unmatch(transactionId: number): Observable<{ transaction: BankTransactionRecord }> {
    return this.http.post<{ transaction: BankTransactionRecord }>(
      `/admin/bank/transactions/${transactionId}/unmatch`,
      {}
    );
  }

  ignore(transactionId: number): Observable<{ transaction: BankTransactionRecord }> {
    return this.http.post<{ transaction: BankTransactionRecord }>(
      `/admin/bank/transactions/${transactionId}/ignore`,
      {}
    );
  }

  unignore(transactionId: number): Observable<{ transaction: BankTransactionRecord }> {
    return this.http.post<{ transaction: BankTransactionRecord }>(
      `/admin/bank/transactions/${transactionId}/unignore`,
      {}
    );
  }

  book(transactionId: number): Observable<{ transaction: BankTransactionRecord }> {
    return this.http.post<{ transaction: BankTransactionRecord }>(
      `/admin/bank/transactions/${transactionId}/book`,
      {}
    );
  }
}
